<script setup lang="ts">
import type { GroupedTableColumn, GroupedTableGroup } from '~~/types';
import type { WikiVehicleUexPrice } from '~~/types/gamedata';

type PriceField = 'price_buy' | 'price_rent';

const props = defineProps({
  vehicleName: {
    type: String,
    required: true,
  },
  purchaseOffers: {
    type: Array as () => WikiVehicleUexPrice[],
    default: () => [],
  },
  rentalOffers: {
    type: Array as () => WikiVehicleUexPrice[],
    default: () => [],
  },
});

const search = ref('');

const rentalDurations = [
  { label: '1 Tag', factor: 1 },
  { label: '3 Tage', factor: 2.625 },
  { label: '7 Tage', factor: 5.25 },
  { label: '30 Tage', factor: 18.75 },
] as const;

const purchaseColumns: GroupedTableColumn[] = [
  { key: 'location', label: 'Location' },
  { key: 'price', label: 'Preis', align: 'right', cellClass: 'whitespace-nowrap font-mono font-medium' },
];

const rentalColumns: GroupedTableColumn[] = [
  { key: 'location', label: 'Location', cellClass: 'min-w-64' },
  ...rentalDurations.map((duration) => ({
    key: `rent-${duration.factor}`,
    label: duration.label,
    align: 'right' as const,
    cellClass: 'whitespace-nowrap font-mono font-medium',
    meta: { factor: duration.factor },
  })),
];

function terminalName(offer: WikiVehicleUexPrice): string {
  return offer.terminal_name?.trim() || 'Unbekanntes Terminal';
}

function systemName(offer: WikiVehicleUexPrice): string {
  return offer.starmap_location?.star_system_name?.trim() || 'Unbekanntes System';
}

function price(offer: WikiVehicleUexPrice, field: PriceField): number {
  return Number(offer[field]);
}

function groupedOffers(offers: WikiVehicleUexPrice[], field: PriceField): GroupedTableGroup<WikiVehicleUexPrice>[] {
  const normalizedSearch = search.value.trim().toLocaleLowerCase('de');
  const groups = new Map<string, WikiVehicleUexPrice[]>();

  for (const offer of offers) {
    if (normalizedSearch && !terminalName(offer).toLocaleLowerCase('de').includes(normalizedSearch)) continue;
    const system = systemName(offer);
    const group = groups.get(system) ?? [];
    group.push(offer);
    groups.set(system, group);
  }

  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right, 'de'))
    .map(([system, systemOffers]) => ({
      key: system,
      label: system,
      rows: systemOffers.sort((left, right) =>
        price(left, field) - price(right, field)
        || terminalName(left).localeCompare(terminalName(right), 'de')),
    }));
}

const purchaseGroups = computed(() => groupedOffers(props.purchaseOffers, 'price_buy'));
const rentalGroups = computed(() => groupedOffers(props.rentalOffers, 'price_rent'));

const cheapestPurchase = computed(() => Math.min(...props.purchaseOffers.map((offer) => price(offer, 'price_buy'))));
const cheapestRental = computed(() => Math.min(...props.rentalOffers.map((offer) => price(offer, 'price_rent'))));

function isCheapest(offer: WikiVehicleUexPrice, field: PriceField): boolean {
  const cheapest = field === 'price_buy' ? cheapestPurchase.value : cheapestRental.value;
  return price(offer, field) === cheapest;
}

function rentalPrice(offer: WikiVehicleUexPrice, factor: number): number {
  return Math.round(price(offer, 'price_rent') * factor);
}

function offerRowKey(offer: WikiVehicleUexPrice, index: number, group: GroupedTableGroup<WikiVehicleUexPrice>): string | number {
  return offer.terminal_id ?? `${String(group.key)}-${index}`;
}

function purchaseRowClass(offer: WikiVehicleUexPrice): string | undefined {
  return isCheapest(offer, 'price_buy') ? 'bg-primary/10' : undefined;
}

function rentalRowClass(offer: WikiVehicleUexPrice): string | undefined {
  return isCheapest(offer, 'price_rent') ? 'bg-primary/10' : undefined;
}

function rentalFactor(column: GroupedTableColumn): number {
  return Number(column.meta?.factor ?? 1);
}
</script>

<template>
  <USlideover
    :title="`In-Game-Preise · ${vehicleName}`"
    description="Kauf- und Mietangebote nach Sternsystem"
    :ui="{ content: 'max-w-5xl', body: 'p-0 sm:p-0' }"
  >
    <UButton
      class="mt-2 w-full justify-center"
      icon="i-lucide-list"
      label="Alle Preise"
      color="neutral"
      variant="outline"
    />

    <template #body>
      <div class="sticky top-0 z-10 border-b border-default bg-default p-4 sm:px-6">
        <UInput
          v-model="search"
          class="w-full"
          icon="i-lucide-search"
          placeholder="Terminal suchen …"
          aria-label="Preise nach Terminal durchsuchen"
        />
      </div>

      <div class="space-y-8 p-4 sm:p-6">
        <section v-if="purchaseOffers.length" aria-labelledby="purchase-prices-title">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 id="purchase-prices-title" class="text-sm font-semibold uppercase tracking-wide text-primary">
              Kaufen
            </h3>
            <span class="text-xs text-muted">{{ purchaseOffers.length }} Angebote</span>
          </div>

          <UiGroupedTable
            :columns="purchaseColumns"
            :groups="purchaseGroups"
            caption="Kaufpreise nach Sternsystem"
            empty-text="Kein Kauf-Terminal entspricht der Suche."
            table-class="min-w-lg"
            :row-key="offerRowKey"
            :row-class="purchaseRowClass"
          >
            <template #cell="{ row: offer, column }">
              <template v-if="column.key === 'location'">
                <div class="flex items-center gap-2">
                  <span>{{ terminalName(offer) }}</span>
                  <UBadge v-if="isCheapest(offer, 'price_buy')" label="Günstigster" color="primary" variant="subtle" size="xs" />
                </div>
              </template>
              <span v-else :class="isCheapest(offer, 'price_buy') ? 'text-primary' : 'text-highlighted'">
                {{ formatCurrency(offer.price_buy) }} <span class="text-xs text-muted">aUEC</span>
              </span>
            </template>
          </UiGroupedTable>
        </section>

        <section v-if="rentalOffers.length" aria-labelledby="rental-prices-title">
          <div class="mb-3 flex items-center justify-between gap-3">
            <h3 id="rental-prices-title" class="text-sm font-semibold uppercase tracking-wide text-primary">
              Mieten
            </h3>
            <span class="text-xs text-muted">{{ rentalOffers.length }} Angebote</span>
          </div>

          <UiGroupedTable
            :columns="rentalColumns"
            :groups="rentalGroups"
            caption="Mietpreise nach Sternsystem"
            empty-text="Kein Miet-Terminal entspricht der Suche."
            table-class="min-w-3xl"
            :row-key="offerRowKey"
            :row-class="rentalRowClass"
          >
            <template #cell="{ row: offer, column }">
              <template v-if="column.key === 'location'">
                <div class="flex items-center gap-2">
                  <span>{{ terminalName(offer) }}</span>
                  <UBadge v-if="isCheapest(offer, 'price_rent')" label="Günstigster" color="primary" variant="subtle" size="xs" />
                </div>
              </template>
              <span v-else :class="isCheapest(offer, 'price_rent') ? 'text-primary' : 'text-highlighted'">
                {{ formatCurrency(rentalPrice(offer, rentalFactor(column))) }} <span class="text-xs text-muted">aUEC</span>
              </span>
            </template>
          </UiGroupedTable>
        </section>
      </div>
    </template>
  </USlideover>
</template>
